'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var React = require('react');
var nthHookTheme = require('@narvar/nth-hook-theme');
var nthHookTranslations = require('@narvar/nth-hook-translations');
var Spinner = require('@narvar/nth-bit-icon-spinner');
var nthBitHeader = require('@narvar/nth-bit-header');
var useFormatEddQuery = require('@narvar/nth-hook-format-edd');
var useOrderTracking = require('@narvar/nth-hook-order-tracking');
var BlockEddDash = require('./BlockEddDash.js');
var useLogEdd = require('./hooks/useLogEdd.js');
var BlockEdd_module = require('./BlockEdd.module.css.js');

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

var React__default = /*#__PURE__*/_interopDefaultLegacy(React);
var Spinner__default = /*#__PURE__*/_interopDefaultLegacy(Spinner);
var useFormatEddQuery__default = /*#__PURE__*/_interopDefaultLegacy(useFormatEddQuery);
var useOrderTracking__default = /*#__PURE__*/_interopDefaultLegacy(useOrderTracking);

var DateTokenType = /*#__PURE__*/function (DateTokenType) {
  DateTokenType["ARIA_LABEL"] = "ARIA_LABEL";
  DateTokenType["DAY_OF_MONTH"] = "DAY_OF_MONTH";
  DateTokenType["DAY_OF_WEEK"] = "DAY_OF_WEEK";
  DateTokenType["MONTH"] = "MONTH";
  return DateTokenType;
}(DateTokenType || {}); // Typescript can only narrow based on literals, meaning we can't determine that
// an arbitrary variable represents a key of an object. In other words, this won't narrow:
// if (token in DateTokenType). But this will: if ("DAY_OF_MONTH" in DateTokenType).
// This utility function asserts that the value of an arbitrary variable is
// in fact a key of an object T if `key in obj` is true
function isIn(key, obj) {
  return key in obj;
}
const DEFAULT_EDD_ORDER = `${DateTokenType.DAY_OF_WEEK},${DateTokenType.MONTH},${DateTokenType.DAY_OF_MONTH}`;
const eddFormatTranslationKeysStandard = [{
  sameDay: 'blockEddTopSameDayDateFormat',
  lastDay: 'blockEddTopLastDayDateFormat',
  nextDay: 'blockEddTopNextDayDateFormat',
  lastWeek: 'blockEddTopLastWeekDateFormat',
  nextWeek: 'blockEddTopNextWeekDateFormat',
  sameElse: 'blockEddTopSameElseDateFormat'
}, {
  sameDay: 'blockEddMiddleSameDayDateFormat',
  lastDay: 'blockEddMiddleLastDayDateFormat',
  nextDay: 'blockEddMiddleNextDayDateFormat',
  lastWeek: 'blockEddMiddleLastWeekDateFormat',
  nextWeek: 'blockEddMiddleNextWeekDateFormat',
  sameElse: 'blockEddMiddleSameElseDateFormat'
}, {
  sameDay: 'blockEddBottomSameDayDateFormat',
  lastDay: 'blockEddBottomLastDayDateFormat',
  nextDay: 'blockEddBottomNextDayDateFormat',
  lastWeek: 'blockEddBottomLastWeekDateFormat',
  nextWeek: 'blockEddBottomNextWeekDateFormat',
  sameElse: 'blockEddBottomSameElseDateFormat'
}];
const eddFormatTranslationKeysLocaleAware = [{
  sameDay: 'blockEddDayOfWeekSameDayDateFormat',
  lastDay: 'blockEddDayOfWeekLastDayDateFormat',
  nextDay: 'blockEddDayOfWeekNextDayDateFormat',
  lastWeek: 'blockEddDayOfWeekLastWeekDateFormat',
  nextWeek: 'blockEddDayOfWeekNextWeekDateFormat',
  sameElse: 'blockEddDayOfWeekSameElseDateFormat'
}, {
  sameDay: 'blockEddMonthSameDayDateFormat',
  lastDay: 'blockEddMonthLastDayDateFormat',
  nextDay: 'blockEddMonthNextDayDateFormat',
  lastWeek: 'blockEddMonthLastWeekDateFormat',
  nextWeek: 'blockEddMonthNextWeekDateFormat',
  sameElse: 'blockEddMonthSameElseDateFormat'
}, {
  sameDay: 'blockEddDayOfMonthSameDayDateFormat',
  lastDay: 'blockEddDayOfMonthLastDayDateFormat',
  nextDay: 'blockEddDayOfMonthNextDayDateFormat',
  lastWeek: 'blockEddDayOfMonthLastWeekDateFormat',
  nextWeek: 'blockEddDayOfMonthNextWeekDateFormat',
  sameElse: 'blockEddDayOfMonthSameElseDateFormat'
}];
const BlockEdd = ({
  featureName,
  internationalDateFormatting
}) => {
  const {
    spinnerPrimaryNone
  } = nthHookTheme.useTheme();
  const translationsQuery = nthHookTranslations.useTranslationsX();
  const {
    getCurrentShipment
  } = useOrderTracking__default["default"]();
  const currentShipment = getCurrentShipment();
  const {
    displayDate,
    displayDateRange,
    scheduling
  } = currentShipment || {};
  const schedulable = scheduling?.isSchedulable && !scheduling.isScheduled;
  const eddFormatTranslationKeys = [{
    sameDay: 'blockEddAriaLabelSameDayDateFormat',
    lastDay: 'blockEddAriaLabelLastDayDateFormat',
    nextDay: 'blockEddAriaLabelNextDayDateFormat',
    lastWeek: 'blockEddAriaLabelLastWeekDateFormat',
    nextWeek: 'blockEddAriaLabelNextWeekDateFormat',
    sameElse: 'blockEddAriaLabelSameElseDateFormat'
  }, {
    sameDay: 'blockEddDayOfWeekShortSameDayDateFormat',
    lastDay: 'blockEddDayOfWeekShortLastDayDateFormat',
    nextDay: 'blockEddDayOfWeekShortNextDayDateFormat',
    lastWeek: 'blockEddDayOfWeekShortLastWeekDateFormat',
    nextWeek: 'blockEddDayOfWeekShortNextWeekDateFormat',
    sameElse: 'blockEddDayOfWeekShortSameElseDateFormat'
  }, ...(internationalDateFormatting ? eddFormatTranslationKeysLocaleAware : eddFormatTranslationKeysStandard)];
  const eddCombos = [];
  const eddBegin = displayDateRange?.begin || displayDate;
  useLogEdd.useLogEdd(currentShipment, featureName);
  if (eddBegin) {
    eddCombos.push({
      displayDate: eddBegin,
      eddFormatTranslationKeys,
      dateOnly: false
    });
  }
  if (displayDateRange?.end) {
    eddCombos.push({
      displayDate: displayDateRange.end,
      eddFormatTranslationKeys,
      dateOnly: false
    });
  }
  const formatEddQuery = useFormatEddQuery__default["default"]({
    skip: !currentShipment,
    eddCombos
  });
  if (translationsQuery.loading || formatEddQuery.loading) {
    return /*#__PURE__*/React__default["default"].createElement("div", null, /*#__PURE__*/React__default["default"].createElement(Spinner__default["default"], {
      s: spinnerPrimaryNone
    }));
  }
  if (!currentShipment || schedulable || !formatEddQuery.data) return null;
  const [eddBeginFormatted, eddEndFormatted] = formatEddQuery.data?.formatEdd.formattedEdds || [];
  let dateValuesMappings = null;
  if (eddEndFormatted) {
    // Use the short "day of week" translation for date range
    const [beginAriaLabel, beginDayOfWeekShort,, beginMonth, beginDayOfMonth] = eddBeginFormatted;
    const [endAriaLabel, endDayOfWeekShort,, endMonth, endDayOfMonth] = eddEndFormatted;
    const begin = beginAriaLabel && beginDayOfWeekShort && beginMonth && beginDayOfMonth ? {
      ARIA_LABEL: beginAriaLabel,
      DAY_OF_MONTH: beginDayOfMonth,
      DAY_OF_WEEK: beginDayOfWeekShort,
      MONTH: beginMonth
    } : null;
    const end = endAriaLabel && endDayOfWeekShort && endMonth && endDayOfMonth ? {
      ARIA_LABEL: endAriaLabel,
      DAY_OF_MONTH: endDayOfMonth,
      DAY_OF_WEEK: endDayOfWeekShort,
      MONTH: endMonth
    } : null;
    dateValuesMappings = [begin, end];
  } else {
    // Use the long "day of week" translation
    const [ariaLabel,, dayOfWeek, month, dayOfMonth] = eddBeginFormatted;
    const begin = ariaLabel && dayOfWeek && month && dayOfMonth ? {
      ARIA_LABEL: ariaLabel,
      DAY_OF_MONTH: dayOfMonth,
      DAY_OF_WEEK: dayOfWeek,
      MONTH: month
    } : null;
    dateValuesMappings = [begin, null];
  }
  if (!dateValuesMappings[0]) return null;
  const blockEddDateTokenOrderRaw = translationsQuery.translations?.blockEddDateTokenOrder;
  const blockEddDateTokenOrder = internationalDateFormatting && typeof blockEddDateTokenOrderRaw === 'string' ? blockEddDateTokenOrderRaw || DEFAULT_EDD_ORDER : DEFAULT_EDD_ORDER;
  const blockEddDateTokenOrderArray = blockEddDateTokenOrder.split(',');
  const renderEdd = dateValuesMapping => {
    if (!dateValuesMapping) return null;
    return blockEddDateTokenOrder.split(',').map(token => {
      let value = null;
      if (isIn(token, dateValuesMapping)) {
        value = dateValuesMapping[token];
      }
      if (!value) return null;
      switch (token) {
        case DateTokenType.DAY_OF_MONTH:
          return /*#__PURE__*/React__default["default"].createElement(nthBitHeader.HugeHeader, {
            as: "p",
            featureName: featureName,
            containerCustomStyle: {
              marginTop: '0.5rem'
            },
            key: token
          }, value);
        case DateTokenType.DAY_OF_WEEK:
          return /*#__PURE__*/React__default["default"].createElement(nthBitHeader.SemibigHeader, {
            as: "p",
            featureName: featureName,
            key: token
          }, value);
        case DateTokenType.MONTH:
          return /*#__PURE__*/React__default["default"].createElement(nthBitHeader.MediumHeader, {
            as: "p",
            featureName: featureName,
            containerCustomStyle: {
              textTransform: 'uppercase'
            },
            key: token
          }, value);
        default:
          return null;
      }
    });
  };
  const [begin, end] = dateValuesMappings;

  // To make sure the hyphen is always in line with the day of month value, the
  // hyphen is given the same margin and lineHeight as the day of month.
  // Then we flex it at the start, center, or end depending on where DAY_OF_MONTH
  // is in the blockEddDateTokenOrderArray
  const containerFlexAlignItems = ['flex-start', 'center', 'flex-end'][blockEddDateTokenOrderArray.indexOf(DateTokenType.DAY_OF_MONTH)];
  return /*#__PURE__*/React__default["default"].createElement("div", {
    "data-test": "eddDisplayContainer",
    className: BlockEdd_module["default"].container,
    style: {
      alignItems: containerFlexAlignItems
    }
  }, begin && /*#__PURE__*/React__default["default"].createElement("div", {
    "aria-label": begin.ARIA_LABEL
  }, /*#__PURE__*/React__default["default"].createElement("span", {
    "aria-hidden": "true"
  }, renderEdd(begin))), end && /*#__PURE__*/React__default["default"].createElement(React__default["default"].Fragment, null, /*#__PURE__*/React__default["default"].createElement(BlockEddDash["default"], null), /*#__PURE__*/React__default["default"].createElement("div", {
    "aria-label": end.ARIA_LABEL
  }, /*#__PURE__*/React__default["default"].createElement("span", {
    "aria-hidden": "true"
  }, renderEdd(end)))));
};
var BlockEdd$1 = nthHookTranslations.withTranslationsProvider(nthHookTheme.withThemeProvider(BlockEdd, {
  themeTokens: ['spinnerPrimaryNone']
}), {
  translationKeys: ['blockEddDateTokenOrder']
});

exports["default"] = BlockEdd$1;
